import styled from '@emotion/styled';
import { useTranslate, T } from '@tolgee/react';
import { Button, Checkbox, Modal, ModalProps } from 'antd';
import { useCreateTrigger } from 'hooks/business/useTrigger';
import { useState } from 'react';
import personImage from 'views/BootGuide/assets/person.png';

function ConfirmModal({ onCancel, onOk, ...otherProps }: ModalProps) {
  const { t } = useTranslate();

  const [checked, setChecked] = useState(false);

  const createTrigger = useCreateTrigger();

  return (
    <Modal
      centered
      width={700}
      closable={false}
      cancelText={t('退出')}
      okText={t('知识有误，确认修改')}
      onCancel={(e) => {
        onCancel?.(e);
        if (checked) {
          createTrigger({
            trigger: 'EDIT_CITE_KNOWLEDGE',
          });
        }
      }}
      onOk={(e) => {
        onOk?.(e);
        onCancel?.(e);
        if (checked) {
          createTrigger({
            trigger: 'EDIT_CITE_KNOWLEDGE',
          });
        }
      }}
      {...otherProps}
    >
      <Container>
        <img src={personImage} alt="person" />
        <div>
          <p>
            <T keyName="cite-knowledge-edit-confirm-tips" params={{ b: <b /> }} />
          </p>
          <p>{t('cite-knowledge-confirm-tips')}</p>
        </div>
        <Checkbox checked={checked} onChange={({ target: { checked } }) => setChecked(checked)}>
          {t('下次不再提醒')}
        </Checkbox>
      </Container>
    </Modal>
  );
}

export interface DeleteConfirmProps {
  mode: 'delete' | 'stop';
  onEdit: () => void;
  onDelete: () => void;
  /** 仅支持直接删除的知识（商详 V2 / 文档知识）没有暂存链路，不提供"仅当前问题不引用"。 */
  onDeleteOnlyCurrent?: () => void;
}

export function DeleteConfirmModal({
  mode,
  onEdit,
  onDelete,
  onDeleteOnlyCurrent,
  onCancel,
  ...otherProps
}: ModalProps & DeleteConfirmProps) {
  const { t } = useTranslate();
  // 局部常量保留可选性收窄，供 onClick 闭包内调用
  const excludeOnly = onDeleteOnlyCurrent;

  return (
    <Modal
      centered
      width={700}
      onCancel={onCancel}
      footer={
        <div>
          <Button onClick={onCancel}>{t('取消')}</Button>
          <Button
            ghost
            type="primary"
            onClick={(e) => {
              onCancel?.(e);
              onDelete();
            }}
          >
            {t(mode === 'delete' ? '删除知识' : '停用知识')}
          </Button>
          {excludeOnly && (
            <Button
              type="primary"
              onClick={(e) => {
                onCancel?.(e);
                excludeOnly();
              }}
            >
              {t('仅当前问题不引用')}
            </Button>
          )}
        </div>
      }
      {...otherProps}
    >
      <Container>
        <img src={personImage} alt="person" />
        <div>
          <div>{t(mode === 'delete' ? '确定要删除当前知识吗？' : '确定要停用当前知识吗？')}</div>
          <p>
            <T
              keyName={
                mode === 'delete'
                  ? '删除知识将影响所有使用该知识的商品！'
                  : '停用知识将影响所有使用该知识的商品！'
              }
              params={{ b: <b /> }}
            />
          </p>
          <ul>
            <li>
              <p>
                <T
                  keyName="若知识内容正确，但该知识不适合回复当前问题，请选择【仅当前问题不引用】"
                  params={{ b: <b /> }}
                />
              </p>
            </li>
            <li>
              <p>
                <T
                  keyName={
                    mode === 'delete'
                      ? '若知识内容错误，您可以选择 修改知识 或【删除知识】'
                      : '若知识内容错误，您可以选择 修改知识 或【停用知识】'
                  }
                  params={{
                    b: <b />,
                    span: (
                      <span
                        onClick={(e) => {
                          onEdit();
                          onCancel?.(e);
                        }}
                      />
                    ),
                  }}
                />
              </p>
            </li>
          </ul>
        </div>
      </Container>
    </Modal>
  );
}

const Container = styled.div`
  position: relative;
  margin: -24px;
  padding: 48px 24px 24px 220px;
  background: linear-gradient(182deg, #c2d4ff 1.56%, #f6f9ff 68.59%);
  > img {
    position: absolute;
    left: 24px;
    bottom: 0;
    width: 175px;
  }
  > label {
    position: absolute;
    left: 24px;
    bottom: -40px;
  }
  > div {
    > div {
      margin-bottom: 16px;
      font-size: 16px;
      font-weight: bold;
    }
    > ul {
      padding-left: 16px;
      p {
        white-space: pre-wrap;
        > span {
          color: var(--ant-primary-color);
          text-decoration: underline;
          cursor: pointer;
        }
      }
    }
  }
`;

export default ConfirmModal;
